import { makeAutoObservable, runInAction } from 'mobx';

import { FEATURED_DEAL_SLUGS } from '@/constants/featuredDeals';
import { actionsService } from '@/services/actions';
import { dealsService } from '@/services/deals';
import {
  ACTION_STATUS,
  TERMINAL_FAILURE_STATUSES,
  TERMINAL_SUCCESS_STATUSES,
  UNRESOLVED_STATUSES,
  type Action,
  type BonusPromo,
  type Deal,
} from '@/types/deal';

/** When a request left: the session and the Action write count (see DealsStore.actionMark). */
export type ActionMark = { readonly epoch: number; readonly seq: number };

/**
 * A resolved deal paired with the terminal action that resolved it. The pair
 * travels together so consumers never re-derive the action from the map —
 * a second lookup could silently disagree with the grouping.
 */
export interface CompletedEntry {
  deal: Deal;
  action: Action;
}

/**
 * The Deals tab's three groups, derived from the user's action per deal.
 * Grouping is presentational — statuses and deal order both come from the
 * server (list is payout-desc); the client only slots deals by the action
 * status it already renders as pills.
 */
export interface DealSections {
  /** Unresolved actions (clicked/pending) — unfinished money, shown first. */
  inProgress: Deal[];
  /** No action yet. Server payout-desc order kept; full deals sink to the end. */
  earnNext: Deal[];
  /** Terminal actions (paid/verified/rejected/expired), most recent first. */
  completed: CompletedEntry[];
}

/**
 * When an action resolved, from the timestamp that matches its terminal
 * status — a stale sibling timestamp must not misorder the list. paid has no
 * paid_at column; verified_at is set on every path to paid (verified → paid
 * is the only transition), so it stands in for both.
 */
function resolvedAtMs(action: Action): number {
  const stamp =
    action.status === ACTION_STATUS.Rejected
      ? action.rejected_at
      : action.status === ACTION_STATUS.Expired
        ? action.expired_at
        : (action.verified_at ?? action.clicked_at);
  return Date.parse(stamp ?? '') || 0;
}

/**
 * Focus-time list refreshes are throttled so tab-hopping doesn't spam the
 * API — cap numbers moving within 30s isn't a state the UI needs to chase.
 */
const LIST_REFRESH_MIN_INTERVAL_MS = 30_000;

/**
 * Deal Steps v2: while a deal screen is open and the action is still in
 * flight, re-fetch that one action so a provider confirmation (which lands
 * minutes after the click) turns a step green without a pull. 20 s is
 * invisible against a 4-minute event and costs ~3 light requests a minute
 * per open screen. A push or socket could call the same refresh later.
 */
export const ACTION_POLL_INTERVAL_MS = 20_000;

/**
 * An action that can no longer change: nothing left to poll for. The server
 * says so (`settled`, Deal Steps v2); the status lists are the fallback for
 * a payload from an older server.
 */
export function isSettled(action: Action | undefined): boolean {
  if (!action) return false;
  if (typeof action.settled === 'boolean') return action.settled;
  return (
    TERMINAL_SUCCESS_STATUSES.includes(action.status) ||
    TERMINAL_FAILURE_STATUSES.includes(action.status)
  );
}

class DealsStore {
  deals: Deal[] = [];
  dealsBySlugMap: Map<string, Deal> = new Map();
  actionsByDealId: Map<number, Action> = new Map();
  /**
   * Server-owned Home-banner payload for a live referral payout boost.
   * null = no banner (no boost, kill switch, or badge-only bonus). Rides
   * the same index fetch as `deals`, so the focus-refresh cadence that
   * keeps cap numbers honest also starts/stops the banner with no OTA.
   */
  bonusPromo: BonusPromo | null = null;

  private lastListRefreshAt = 0;
  // Set true after the first successful load(). Distinguishes "never loaded"
  // (cold start, or retry after a failed load) from "loaded and the server
  // returned zero deals". Without it, an empty list re-triggers a full load()
  // on every tab focus — a skeleton flash plus a needless request each visit.
  private hasLoadedOnce = false;

  /**
   * True once a list fetch has succeeded, even with zero deals. Screens
   * that key a "not here" state on `deals.length` alone can never tell an
   * empty list from a list that hasn't arrived.
   */
  get hasLoaded(): boolean {
    return this.hasLoadedOnce;
  }
  // A list fetch (refreshList / pullRefreshList) is in flight. Keeps the quiet
  // background refresh and the manual pull from racing each other, or a cold
  // load(), into overlapping requests with last-write-wins ordering.
  private listFetching = false;

  loading = false;
  error: string | null = null;

  clicking = false;
  clickError: string | null = null;

  advancing = false;
  advanceError: string | null = null;

  refreshing = false;
  refreshError: string | null = null;

  // Pull-to-refresh spinners for the list and the deal detail. Distinct from
  // `refreshing` (single-action refresh) and `loading` (cold-start skeleton):
  // a manual pull is explicit intent, so it shows a spinner and bypasses the
  // focus-refresh throttle.
  pullRefreshingList = false;
  pullRefreshingDeal = false;

  /** Deal id currently being polled for confirmations, or null. */
  pollingDealId: number | null = null;
  private actionPollTimer: ReturnType<typeof setInterval> | null = null;
  // Bumped every time a user action (advance) stores a newer Action. A
  // refresh/poll that started before the bump discards its response instead
  // of overwriting the newer state with an older payload (the poll GET can
  // resolve after the advance POST it raced with).
  private actionMutationSeq = 0;
  // Bumped by reset() (logout): a response to a request the previous user
  // made belongs to no one and is dropped.
  private sessionEpoch = 0;
  // Per-deal request ordering for refreshAction: a response from an older
  // request never overwrites one from a newer request (focus refresh and an
  // AppState refresh can overlap; the network may return them out of order).
  private refreshSeq = 0;
  private appliedRefreshSeq = new Map<number, number>();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * Start the 20 s confirmation poll for one deal's action (deal screen in
   * focus). Idempotent per deal: calling again for the same deal keeps the
   * running timer; a different deal replaces it. Stops itself once the
   * action settles (paid / rejected / expired), since nothing can change.
   * Failures keep the cached action and the timer keeps going.
   */
  startActionPolling(dealId: number): void {
    if (this.actionPollTimer && this.pollingDealId === dealId) return;
    this.stopActionPolling();
    runInAction(() => {
      this.pollingDealId = dealId;
    });
    this.actionPollTimer = setInterval(() => {
      void this.pollActionOnce(dealId);
    }, ACTION_POLL_INTERVAL_MS);
  }

  stopActionPolling(): void {
    if (this.actionPollTimer) {
      clearInterval(this.actionPollTimer);
      this.actionPollTimer = null;
    }
    runInAction(() => {
      this.pollingDealId = null;
    });
  }

  private async pollActionOnce(dealId: number): Promise<void> {
    try {
      await this.refreshAction(dealId);
    } catch (err) {
      if (__DEV__) console.warn('[deals] confirmation poll failed', err);
    }
    if (this.pollingDealId === dealId && isSettled(this.actionsByDealId.get(dealId))) {
      this.stopActionPolling();
    }
  }

  /**
   * Number of deals the user has started. Home labels this "Active deals",
   * but product wants a completed/paid deal to still count here: the stat is
   * about participation, not unresolved work.
   */
  get activeCount(): number {
    return this.actionsByDealId.size;
  }

  /**
   * See DealSections. `verified` counts as completed, not in-progress —
   * the user has nothing left to do, the payout is queued server-side.
   * In-progress sorts by when the user started (most recent first);
   * completed by when the action resolved. MarketPryce Social deals are left
   * out: they live on the Create tab.
   */
  get sections(): DealSections {
    const inProgress: Deal[] = [];
    const earnNext: Deal[] = [];
    const fullDeals: Deal[] = [];
    const completed: CompletedEntry[] = [];

    for (const deal of this.deals) {
      if (deal.social) continue;
      const action = this.actionsByDealId.get(deal.id);
      if (!action) {
        (deal.full ? fullDeals : earnNext).push(deal);
      } else if (UNRESOLVED_STATUSES.includes(action.status)) {
        inProgress.push(deal);
      } else {
        completed.push({ deal, action });
      }
    }

    const startedAt = (deal: Deal): number =>
      Date.parse(this.actionsByDealId.get(deal.id)?.clicked_at ?? '') || 0;
    inProgress.sort((a, b) => startedAt(b) - startedAt(a));
    completed.sort((a, b) => resolvedAtMs(b.action) - resolvedAtMs(a.action));

    return { inProgress, earnNext: [...earnNext, ...fullDeals], completed };
  }

  /**
   * The single deal to pin in Home's featured slot, or undefined when nothing
   * qualifies (slot renders nothing). Like `sections`, this is presentational:
   * the ranking is editorial (`FEATURED_DEAL_SLUGS`) and everything else is
   * the same deal×action join the tab already does — no server round trip,
   * because `fetchListInto` already loads both halves.
   *
   * Iterates the constant rather than the deal list so the array IS the
   * order — no sort, no rank field to compare.
   *
   * A deal is skipped when the user has ANY action on it, not just a terminal
   * one. Re-pitching something they've already started is what the Deals tab's
   * "In progress" section is for; this slot is for the next thing to start.
   */
  get featuredDeal(): Deal | undefined {
    for (const slug of FEATURED_DEAL_SLUGS) {
      const deal = this.dealsBySlugMap.get(slug);
      if (!deal || deal.full || this.actionsByDealId.has(deal.id)) continue;
      return deal;
    }
    return undefined;
  }

  bySlug(slug: string): Deal | undefined {
    return this.dealsBySlugMap.get(slug);
  }

  actionFor(dealId: number): Action | undefined {
    return this.actionsByDealId.get(dealId);
  }

  /**
   * Fetches the deal list plus the current user's actions for those deals and
   * swaps both into the store. The shared core of load / refreshList /
   * pullRefreshList — each wraps this with its own spinner and error policy.
   * Sequential by design: the actions request needs the deal IDs we just
   * fetched. Marks the store loaded so an empty result stops re-triggering the
   * cold load, and clears any prior error — a successful fetch (even an empty
   * one) means we're healthy again, so the error/retry UI must not linger.
   */
  private async fetchListInto(): Promise<void> {
    const { deals, bonus_promo } = await dealsService.list();
    const dealIds = deals.map((d) => d.id);
    const actions = dealIds.length > 0 ? await actionsService.list(dealIds) : [];
    runInAction(() => {
      this.deals = deals;
      this.dealsBySlugMap = new Map(deals.map((d) => [d.slug, d]));
      this.actionsByDealId = new Map(actions.map((a) => [a.deal_id, a]));
      this.bonusPromo = bonus_promo;
      this.hasLoadedOnce = true;
      this.error = null;
    });
  }

  /**
   * Fetches one deal and replace-or-appends it into the store (appends when
   * it's not loaded yet — deep link / push before the list loads). The shared
   * core of refreshDeal / pullRefreshDeal.
   */
  private async fetchDealInto(slug: string): Promise<void> {
    const fresh = await dealsService.show(slug);
    runInAction(() => {
      this.deals = this.deals.some((d) => d.id === fresh.id)
        ? this.deals.map((d) => (d.id === fresh.id ? fresh : d))
        : [...this.deals, fresh];
      const nextBySlug = new Map(this.dealsBySlugMap);
      nextBySlug.set(fresh.slug, fresh);
      this.dealsBySlugMap = nextBySlug;
    });
  }

  /**
   * Idempotent loader for screen mount effects. Runs the cold load exactly
   * once: it no-ops after the first successful load (even if the server
   * returned zero deals), so a legitimately empty list doesn't reload on
   * every focus. A failed load leaves `hasLoadedOnce` false, so this still
   * retries. Ongoing revalidation (including of an empty list) is
   * refreshList's throttled job. Components that depend on the deals/actions
   * data call this in their mount effect rather than wiring their own guard.
   */
  ensureLoaded(): Promise<void> {
    if (this.loading) return Promise.resolve();
    if (this.hasLoadedOnce) return Promise.resolve();
    return this.load().catch(() => {
      // Error captured on store.error; callers don't need to handle.
    });
  }

  /**
   * Tab-focus revalidation of the whole list (deals + actions), so the
   * index's cap numbers stay honest across a session — and so a list that
   * loaded empty picks up newly-activated deals without an app restart.
   * Quiet like refreshDeal: no loading flag (the list keeps rendering, fresh
   * data swaps in place), failures keep the cached list. No-ops until the
   * first load has completed — cold start belongs to ensureLoaded, and
   * running both on first focus would double-fetch — then throttles to one
   * refetch per LIST_REFRESH_MIN_INTERVAL_MS after a success (a failed refresh
   * doesn't start the clock, so the next focus can retry). Skips while another
   * list fetch is already in flight.
   */
  async refreshList(): Promise<void> {
    if (!this.hasLoadedOnce || this.loading || this.listFetching) return;
    if (Date.now() - this.lastListRefreshAt < LIST_REFRESH_MIN_INTERVAL_MS) return;
    runInAction(() => {
      this.listFetching = true;
    });

    try {
      await this.fetchListInto();
      // Stamp only on success: a failed refresh must not start the throttle
      // clock, or the next focus would be starved of a retry for up to 30s.
      runInAction(() => {
        this.lastListRefreshAt = Date.now();
      });
    } catch (err) {
      // Background refresh: cached data is the fallback, nothing to surface.
      if (__DEV__) console.warn('[deals] background list refresh failed', err);
    } finally {
      runInAction(() => {
        this.listFetching = false;
      });
    }
  }

  /**
   * Pull-to-refresh on the deals list. Explicit user intent, so unlike
   * refreshList it shows a spinner and ignores the focus throttle. On success
   * it stamps lastListRefreshAt so the next tab focus doesn't immediately
   * refetch on top of it. Re-entrant pulls are ignored, and it skips while a
   * background refresh or cold load is already fetching the list.
   */
  async pullRefreshList(): Promise<void> {
    if (this.pullRefreshingList || this.loading || this.listFetching) return;
    runInAction(() => {
      this.pullRefreshingList = true;
      this.listFetching = true;
    });
    try {
      await this.fetchListInto();
      runInAction(() => {
        this.lastListRefreshAt = Date.now();
      });
    } catch (err) {
      if (__DEV__) console.warn('[deals] pull-to-refresh list failed', err);
    } finally {
      runInAction(() => {
        this.pullRefreshingList = false;
        this.listFetching = false;
      });
    }
  }

  /**
   * Re-fetches one deal and replaces it in the store, so screens showing
   * a single deal get live cap numbers (spots_left burns down all day;
   * the load-once list goes stale within a session). Stale-while-
   * revalidate: callers keep rendering the cached deal and this swaps the
   * fresh one in place — no loading flag, and a failure keeps the cached
   * deal (the server still enforces the cap at click time regardless).
   */
  async refreshDeal(slug: string): Promise<void> {
    try {
      await this.fetchDealInto(slug);
    } catch (err) {
      // Background refresh: cached data is the fallback, nothing to surface.
      if (__DEV__) console.warn('[deals] background deal refresh failed', err);
    }
  }

  /**
   * Pull-to-refresh on a deal detail — visible-spinner variant of refreshDeal.
   * Also re-reads the user's progress with a provider sync, so pulling is how
   * someone waiting on a brand (WELD shipping) asks for the latest.
   */
  async pullRefreshDeal(slug: string, dealId?: number): Promise<void> {
    if (this.pullRefreshingDeal) return;
    runInAction(() => {
      this.pullRefreshingDeal = true;
    });
    try {
      await Promise.all([
        this.fetchDealInto(slug),
        dealId !== undefined ? this.refreshAction(dealId, { sync: true }) : Promise.resolve(),
      ]);
    } catch (err) {
      if (__DEV__) console.warn('[deals] pull-to-refresh deal failed', err);
    } finally {
      runInAction(() => {
        this.pullRefreshingDeal = false;
      });
    }
  }

  /**
   * Loads deals first, then the current user's actions filtered to those
   * deal IDs. Sequential by design — the actions request needs the deal
   * IDs we just fetched so we don't pull actions for deals that are no
   * longer visible. Guards against concurrent calls (later wins);
   * `loading` reflects the most recent call.
   */
  async load(): Promise<void> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });
    try {
      await this.fetchListInto();
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load deals.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Records a click on a deal and refreshes the action so the UI reflects
   * the new clicked status immediately. Returns the redirect URL the
   * caller should open in WebBrowser. Refreshes even when the deal isn't
   * in the local store yet (deep link / push notification before load).
   */
  async click(slug: string): Promise<{ redirectUrl: string }> {
    runInAction(() => {
      this.clicking = true;
      this.clickError = null;
    });
    try {
      const response = await dealsService.click(slug);
      const deal = this.bySlug(slug);
      if (deal) {
        await this.refreshAction(deal.id);
      }
      return { redirectUrl: response.redirect_url };
    } catch (err) {
      runInAction(() => {
        this.clickError = err instanceof Error ? err.message : 'Click failed.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.clicking = false;
      });
    }
  }

  /**
   * Refresh a single action — call after WebBrowser dismiss to pull
   * verified/pending. If the server returns no action for this deal
   * (transient filter mismatch, eventual consistency), keep the existing
   * entry rather than dropping it. The Action only goes away on hard
   * server signals (404 / explicit terminal status).
   */
  async refreshAction(dealId: number, { sync = false }: { sync?: boolean } = {}): Promise<void> {
    runInAction(() => {
      this.refreshing = true;
      this.refreshError = null;
    });
    const mark = this.actionMark();
    const mySeq = ++this.refreshSeq;
    try {
      const actions = await actionsService.list([dealId], { sync });
      const fresh = actions[0];
      if (!fresh) return;
      // Stale: a logout, or the user advanced a step while this request was
      // in flight (the advance response is newer), or a later refresh for
      // this deal already landed. Either way the server state here is older
      // than what the store holds.
      if (!this.isCurrent(mark, { snapshot: true })) return;
      if (mySeq < (this.appliedRefreshSeq.get(dealId) ?? 0)) return;
      this.appliedRefreshSeq.set(dealId, mySeq);
      runInAction(() => {
        const next = new Map(this.actionsByDealId);
        next.set(dealId, fresh);
        this.actionsByDealId = next;
      });
    } catch (err) {
      runInAction(() => {
        this.refreshError = err instanceof Error ? err.message : 'Refresh failed.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.refreshing = false;
      });
    }
  }

  /**
   * Adopt an Action the server returned from another endpoint (the Voltr
   * apply / order calls). Bumps the mutation seq so an in-flight refresh
   * can't overwrite it with older state.
   */
  applyAction(action: Action): void {
    this.actionMutationSeq += 1;
    const next = new Map(this.actionsByDealId);
    next.set(action.deal_id, action);
    this.actionsByDealId = next;
  }

  /** Take before an await: the session, and the latest Action write, a response can be checked against. */
  actionMark(): ActionMark {
    return { epoch: this.sessionEpoch, seq: this.actionMutationSeq };
  }

  /**
   * Still the session `mark` was taken in. A `snapshot` (a read, like
   * /social or a refresh) also needs no Action written since: a newer
   * mutation's answer beats it.
   */
  isCurrent(mark: ActionMark, { snapshot = false }: { snapshot?: boolean } = {}): boolean {
    return mark.epoch === this.sessionEpoch && (!snapshot || mark.seq === this.actionMutationSeq);
  }

  /** applyAction for a response to a request sent at `mark`, dropped when it's no longer current. True when applied. */
  applyActionSince(mark: ActionMark, action: Action, opts: { snapshot?: boolean } = {}): boolean {
    if (!this.isCurrent(mark, opts)) return false;
    this.applyAction(action);
    return true;
  }

  /** Step CTA tap. Server completes the step; we replace the action in-place. */
  async advance(actionId: number, stepKey: string): Promise<void> {
    runInAction(() => {
      this.advancing = true;
      this.advanceError = null;
    });
    const mark = this.actionMark();
    try {
      const action = await actionsService.advance(actionId, stepKey);
      runInAction(() => {
        if (!this.isCurrent(mark)) return;
        this.actionMutationSeq += 1;
        const next = new Map(this.actionsByDealId);
        next.set(action.deal_id, action);
        this.actionsByDealId = next;
      });
    } catch (err) {
      runInAction(() => {
        this.advanceError = err instanceof Error ? err.message : 'Step failed.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.advancing = false;
      });
    }
  }

  reset(): void {
    this.stopActionPolling();
    runInAction(() => {
      this.sessionEpoch += 1;
      this.deals = [];
      this.dealsBySlugMap = new Map();
      this.actionsByDealId = new Map();
      this.bonusPromo = null;
      this.lastListRefreshAt = 0;
      this.hasLoadedOnce = false;
      this.listFetching = false;
      this.loading = false;
      this.error = null;
      this.clicking = false;
      this.clickError = null;
      this.advancing = false;
      this.advanceError = null;
      this.refreshing = false;
      this.refreshError = null;
      this.pullRefreshingList = false;
      this.pullRefreshingDeal = false;
    });
  }
}

export const dealsStore = new DealsStore();
// `DealsStore` class is exported for tests so each test gets a fresh
// instance. Production code should use the `dealsStore` singleton.
export { DealsStore };
